import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { ReversementDu } from '@troc/shared'
import { eur, libellePeriode } from '@troc/shared'
import { api } from '../api'
import { Ecran } from '../composants/Ecran'
import { PastilleReversement } from '../composants/Pastille'
import { useEstVisible, useReveil } from '../composants/Volets'

type LigneVersement = ReversementDu & { contact: string; iban: string }

interface LigneDetail {
  ref: string
  libelle: string
  categorie: string
  quantite: number
  prixCentimes: number
  taux: number
  netCentimes: number
  venduLe: string
  echu: boolean
}

interface Etat {
  periode: string
  payable: boolean
  totalPayableCentimes: number
  totalAttenteCentimes: number
  deposants: LigneVersement[]
}

/**
 * Ce que la boutique doit à ses déposants.
 *
 * Cette liste était au bas du Bilan, derrière le code gérant. Payer un client
 * qui se présente est pourtant un geste de comptoir : il lui faut son écran, à
 * portée de doigt comme la Vente ou le Dépôt.
 *
 * Deux colonnes de montants, jamais confondues : **payable** est ce qu'on peut
 * sortir de la caisse aujourd'hui, **en attente** ce qui est acquis au client
 * mais dont le 15 n'est pas encore passé.
 */
export function Versements() {
  const [etat, setEtat] = useState<Etat | null>(null)
  const [chargement, setChargement] = useState(true)
  const [recherche, setRecherche] = useState('')
  /**
   * Le client dont on regarde le détail, et ses lignes.
   *
   * Annoncer « 8 € » sans pouvoir dire ce qui a été vendu met le vendeur en
   * porte-à-faux : le client, lui, se souvient de ses pièces. Une fenêtre à
   * part plutôt qu'une ligne dépliée : un déposant fidèle peut avoir plusieurs
   * centaines de ventes, et les pousser dans le tableau des clients rendrait
   * la liste illisible.
   */
  const [ouvert, setOuvert] = useState<LigneVersement | null>(null)
  const [detail, setDetail] = useState<LigneDetail[] | null>(null)
  const visible = useEstVisible('/versements')

  const charger = useCallback(() => {
    setChargement(true)
    api<Etat>('/versements')
      .then(setEtat)
      .catch(() => undefined)
      .finally(() => setChargement(false))
  }, [])

  useEffect(() => {
    charger()
  }, [charger])

  const ouvrir = (ligne: LigneVersement) => {
    setOuvert(ligne)
    setDetail(null)
    api<{ lignes: LigneDetail[] }>(`/versements/${ligne.deposantId}`)
      .then((r) => setDetail(r.lignes))
      .catch(() => setDetail([]))
  }
  // Au retour sur l'écran, les montants ont pu changer : une vente encaissée
  // entre-temps augmente ce qu'on doit.
  useReveil('/versements', charger)

  const lignes = (etat?.deposants ?? []).filter((l) =>
    recherche
      ? `${l.deposantNom} ${l.deposantRef} ${l.contact}`
          .toLowerCase()
          .includes(recherche.toLowerCase())
      : true,
  )

  const virements = lignes.filter((l) => l.mode === 'virement').length

  return (
    <Ecran
      titre="Versements"
      sousTitre={
        etat
          ? `${eur(etat.totalPayableCentimes)} à reverser · ${etat.deposants.length} client${
              etat.deposants.length > 1 ? 's' : ''
            }`
          : 'Chargement…'
      }
      outils={
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            className="saisie saisie-grande"
            style={{ width: 320, maxWidth: '34vw' }}
            placeholder="Nom, n° de client, téléphone…"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
          />
        </div>
      }
    >
      <div className="zone-defilante" style={{ padding: '20px 24px' }}>
        {etat && (
          <div className="versements-entete">
            <div className="carte-chiffre">
              <div className="etiquette">
                {etat.payable ? 'Payable aujourd’hui' : 'À préparer — guichet fermé jusqu’au 15'}
              </div>
              <div className="chiffre grand">{eur(etat.totalPayableCentimes)}</div>
              <div className="sourdine">
                {etat.deposants.length} client{etat.deposants.length > 1 ? 's' : ''}
                {virements > 0 && ` · ${virements} par virement (plus de 3 000 €)`}
              </div>
            </div>
            <div className="carte-chiffre">
              <div className="etiquette">En attente</div>
              <div className="chiffre grand sourdine">{eur(etat.totalAttenteCentimes)}</div>
              <div className="sourdine">
                acquis aux clients, payable au 15 — période {libellePeriode(etat.periode)}
              </div>
            </div>
          </div>
        )}

        <table className="tableau">
          <thead>
            <tr>
              <th>N°</th>
              <th>Déposant</th>
              <th>Téléphone</th>
              <th className="a-droite">Ventes</th>
              <th className="a-droite">Payable</th>
              <th className="a-droite">En attente</th>
              <th>Mode</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => (
              <tr key={l.deposantId} onClick={() => ouvrir(l)} style={{ cursor: 'pointer' }}>
                <td className="ref">{l.deposantRef}</td>
                <td className="titre">{l.deposantNom}</td>
                <td>{l.contact || '—'}</td>
                <td className="a-droite">{l.nbVentes}</td>
                <td className="a-droite chiffre">{eur(l.montantCentimes)}</td>
                <td className="a-droite sourdine">
                  {l.attenteCentimes > 0 ? eur(l.attenteCentimes) : '—'}
                </td>
                <td>{l.montantCentimes > 0 && <PastilleReversement mode={l.mode} />}</td>
                <td className="a-droite">
                  <Link
                    className="btn btn-fantome"
                    to={`/deposants/${l.deposantId}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Payer
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {lignes.length === 0 && (
          <div className="vide">
            {chargement
              ? 'Chargement…'
              : recherche
                ? 'Aucun client ne correspond.'
                : 'Rien à reverser — tout est à jour.'}
          </div>
        )}
        {!visible && null}
      </div>

      {ouvert && (
        <DetailVersement
          ligne={ouvert}
          detail={detail}
          onFermer={() => setOuvert(null)}
        />
      )}
    </Ecran>
  )
}

/**
 * Le détail des ventes d'un déposant, dans une fenêtre à part.
 *
 * Une ligne dépliée dans le tableau des versements fonctionnait pour dix
 * ventes ; un déposant fidèle en a parfois plusieurs centaines, et la liste
 * générale devenait illisible dès qu'on l'ouvrait. Ici, elle a toute la place.
 */
function DetailVersement({
  ligne,
  detail,
  onFermer,
}: {
  ligne: LigneVersement
  detail: LigneDetail[] | null
  onFermer: () => void
}) {
  return (
    <div className="voile voile-centre" onMouseDown={onFermer}>
      <div
        className="panneau-cloture"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="panneau-entete">
          <h4 style={{ margin: 0 }}>
            {ligne.deposantRef} · {ligne.deposantNom}
          </h4>
          <button type="button" className="btn btn-fantome" onClick={onFermer}>
            Fermer
          </button>
        </div>

        <div className="cloture-chiffres">
          <div>
            <span className="etiquette">Payable</span>
            <div className="chiffre" style={{ fontSize: 30 }}>{eur(ligne.montantCentimes)}</div>
          </div>
          <div>
            <span className="etiquette">En attente</span>
            <div className="chiffre" style={{ fontSize: 30 }}>
              {ligne.attenteCentimes > 0 ? eur(ligne.attenteCentimes) : '—'}
            </div>
          </div>
          <div>
            <span className="etiquette">Ventes</span>
            <div className="chiffre" style={{ fontSize: 30 }}>{ligne.nbVentes}</div>
          </div>
        </div>

        <div className="zone-defilante" style={{ padding: '0 18px' }}>
          {detail === null ? (
            <div className="vide">Chargement du détail…</div>
          ) : detail.length === 0 ? (
            <div className="vide">Aucune ligne.</div>
          ) : (
            <table className="tableau">
              <thead>
                <tr>
                  <th>Vendu le</th>
                  <th>Réf.</th>
                  <th>Article</th>
                  <th className="a-droite">Qté</th>
                  <th className="a-droite">Prix</th>
                  <th className="a-droite">Sa part</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {detail.map((d, i) => (
                  <tr key={`${d.ref}-${i}`}>
                    <td>{new Date(d.venduLe).toLocaleDateString('fr-FR')}</td>
                    <td className="ref">{d.ref}</td>
                    <td>{d.libelle}</td>
                    <td className="a-droite">{d.quantite}</td>
                    <td className="a-droite chiffre">{eur(d.prixCentimes)}</td>
                    <td className="a-droite chiffre">{eur(d.netCentimes)}</td>
                    <td className="sourdine">{d.echu ? '' : 'payable au 15'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="panneau-pied">
          <Link
            className="btn btn-primaire btn-large"
            style={{ flex: 1 }}
            to={`/deposants/${ligne.deposantId}`}
          >
            Aller à la fiche — Payer {eur(ligne.montantCentimes)}
          </Link>
        </div>
      </div>
    </div>
  )
}
